import { useState } from "react";
import { useDispatch } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { getImage } from "redux/slices/getImageSlice";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";
import {
  getTaxStatus,
  getCompanyNameByBlockCode,
  base64ToArrayBuffer,
  getBadgeClassByPolicyStatus,
  getBadgeText,
  stringFormattingToCurrency,
} from "js/formatting";
import { isAnnuityPolicyInactive } from "js/utilities";

import ContactUsDialog from "components/ModalDialogs/ContactUsDialog";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import PDFDialog from "components/ModalDialogs/PDFDialog";
import TextTooltip from "components/General/TextTooltip";
import CardWithFields from "components/General/CardWithFields";
import config from "config/config";
import * as fileSaver from "file-saver";
import moment from "moment";
import Badge from "components/General/Badge";

const DeferredCoverageAndDocuments = ({
  policy,
  filteredDocuments,
  deathBenefitValue,
}) => {
  const { t } = useTranslation();
  let dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [pdfSource, setPdfSource] = useState("");
  const [desc, setDesc] = useState("");
  const [pdfDialogState, setPdfDialogState] = useState(false);
  const [name, setName] = useState("");
  const [contactUsDialogState, setContactUsDialogState] = useState(false);
  const maturityDate =
    policy?.oldPolicy?.substring(0, 2) === "AC" ? (
      <TextTooltip
        labelText={t(
          "CustomerPolicyDetails-coverage-documents-deferred-annuity-text-tooltip-title"
        )}
        content={t(
          "CustomerPolicyDetails-coverage-documents-deferred-annuity-text-tooltip-content"
        )}
        placement="right"
      />
    ) : (
      policy?.maturityDate && moment(policy?.maturityDate).format("MM/DD/YYYY")
    );

  const isInactiveStatus = isAnnuityPolicyInactive(policy);

  const AnnuityDocuments = () => (
    <>
      <EverlakeLoadingOverlay isLoading={loading}>
        {filteredDocuments?.length > 0 && (
          <>
            <div className="px-4 pb-3 xl:px-6 xl:pb-6">
              <div className="font-semibold mt-6 mb-1">
                {t(
                  "CustomerPolicyDetails-coverage-documents-deferred-annuity-documents"
                )}
              </div>
              <div className="ui-download-document">
                {filteredDocuments?.map((document, index) => {
                  if (
                    document?.document?.typeOfDocument?.toUpperCase() === "TAX"
                  ) {
                    return (
                      <div
                        className="inline-block p-2 border border-ui-gray-light-medium rounded"
                        key={`document${index}`}
                      >
                        <div className="text-xs font-semibold text-theme-text-secondary whitespace-nowrap">
                          {document?.document?.typeOfDocument?.toUpperCase() ===
                            "TAX" && t("FairMarketValueLetters-des")}
                        </div>
                        <div>
                          <button
                            className="ui-link-icon font-bold text-xs"
                            onClick={() => {
                              if (document?.document?.name) {
                                setLoading(true);
                                dispatch(
                                  getImage({
                                    imageFileName: document.document.name,
                                    policyId: document.policy.contractID,
                                  })
                                )
                                  .then((response) => {
                                    setLoading(false);
                                    let byteArray = base64ToArrayBuffer(
                                      response.payload.document.contentStream
                                        .type
                                    );
                                    let blob = new Blob([byteArray], {
                                      type: "application/pdf",
                                    });
                                    fileSaver.saveAs(
                                      blob,
                                      `${document.document.name}.pdf`
                                    );
                                  })
                                  .catch((error) => {
                                    console.log("Error", error.message);
                                    setName(document?.document?.name);
                                    setPdfDialogState(true);
                                    setLoading(false);
                                    setDesc(document?.document?.description);
                                    setPdfSource("");
                                  });
                              }
                            }}
                          >
                            <svg
                              className="ui-icon w-4 h-4"
                              focusable="false"
                              aria-hidden="true"
                              role="presentation"
                            >
                              <use href="#ui-icon-document-download"></use>
                            </svg>
                            {t(
                              "CustomerPolicyDetails-coverage-documents-deferred-annuity-download-pdf"
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </>
        )}
      </EverlakeLoadingOverlay>
    </>
  );

  const BadgeComponent = ({ policy }) => {
    return (
      <>
        {policy?.status && policy?.status !== "" && (
          <div className="hidden md:block">
            <Badge
              status={getBadgeClassByPolicyStatus(policy)}
              statusText={getBadgeText(policy)}
              srPrefix="Policy"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <CardWithFields
        additionalContent={
          <>
            <AnnuityDocuments />{" "}
            <CardDisclaimerBase
              disclaimer={
                <Trans
                  i18nKey={
                    "CustomerPolicyDetails-coverage-documents-annualstmt-disclaimer"
                  }
                  components={{
                    button1: (
                      <button
                        onClick={() => setContactUsDialogState(true)}
                        className="ui-btn-hyperlink text-xs font-semibold ml-0"
                      ></button>
                    ),
                  }}
                />
              }
            />{" "}
          </>
        }
        additionalChildComponent={<BadgeComponent policy={policy} />}
        badgeClass={getBadgeClassByPolicyStatus(policy)}
        ariaLabel={t(
          "CustomerPolicyDetails-coverage-documents-deferred-annuity-aria-label"
        )}
        cardTitle={t(
          "CustomerPolicyDetails-coverage-documents-deferred-annuity-card-title"
        )}
        cardFields={[
          {
            title: t(
              "CustomerPolicyDetails-coverage-documents-deferred-annuity-company"
            ),
            value: getCompanyNameByBlockCode(policy?.blockCode),
            hidden:
              config.ENABLE_SHOW_COMPANY_NAME_ON_ANNUITY_POLICIES === "false",
          },
          {
            title: t(
              "CustomerPolicyDetails-coverage-documents-deferred-annuity-contract-number"
            ),
            value: policy?.oldPolicy,
          },
          {
            title: t(
              "CustomerPolicyDetails-coverage-documents-deferred-annuity-product-name"
            ),
            value: policy?.product?.description,
          },
          {
            title: t(
              "CustomerPolicyDetails-coverage-documents-deferred-annuity-tax-qualification"
            ),
            value: getTaxStatus(policy?.tax),
          },
          {
            title: t(
              "CustomerPolicyDetails-coverage-documents-deferred-annuity-issue-date"
            ),
            value:
              policy?.effectiveDate &&
              moment(policy?.effectiveDate).format("MM/DD/YYYY"),
          },
          {
            title: t(
              "CustomerPolicyDetails-coverage-documents-deferred-annuity-maturity-date"
            ),
            value: maturityDate,
          },
          {
            title: t(
              "CustomerPolicyDetails-coverage-documents-deferred-annuity-death-benefit"
            ),
            value: stringFormattingToCurrency(deathBenefitValue),
          },
        ]}
      />
      <PDFDialog
        title={`${name ?? ""} Preview`}
        description={desc}
        pdfSource={pdfSource}
        dialogState={pdfDialogState}
        setDialogState={setPdfDialogState}
      />
      <ContactUsDialog
        dialogState={contactUsDialogState}
        setDialogState={setContactUsDialogState}
      />
    </>
  );
};

export default DeferredCoverageAndDocuments;
